import { EnvironmentName } from "@inrev/common";
import { initializeCommonLibraryContext } from "@inrev/common/lib/esm/context";
import * as Sentry from "@sentry/react";
import { Settings } from "luxon";
import React from "react";
import "react-day-picker/dist/style.css";
import ReactDOM from "react-dom/client";
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from "react-router-dom";
import App from "./App.tsx";
import { getFromValidatedAddressCache, setValidatedAddressCache } from "./api.ts";
import { type AppConfig, loadConfig } from "./config.ts";
import "./index.css";
import { initAmplitude } from "./analytics.ts";
import { reportReactRootError } from "./utils/error.ts";
import { loadGoogleMaps } from "./utils/loadGoogleMaps.ts";
import { initializeMimeTypes } from "./utils/mime.ts";
import { logDataToSentry } from "./utils/sentry.ts";
import "./utils/zod-error-map";

Settings.throwOnInvalid = true;

declare module "luxon" {
	export interface TSSettings {
		throwOnInvalid: true;
	}
}

function getEnvironmentName(env: AppConfig["environment"]): EnvironmentName {
	switch (env) {
		case "prod":
			return EnvironmentName.production;
		case "qa":
			return EnvironmentName.qa;
		case "demo":
			return EnvironmentName.demo;
		case "local":
		default:
			return EnvironmentName.local;
	}
}

async function initializeApp(config: AppConfig) {
	if (config.googleAddressKey) {
		await loadGoogleMaps(config.googleAddressKey);
	}

	initializeCommonLibraryContext({
		config: {
			googleAddressApiKey: config.googleAddressKey,
			environment: getEnvironmentName(config.environment),
		},
		checkValidAddressCacheFn: getFromValidatedAddressCache,
		setValidAddressCacheFn: setValidatedAddressCache,
		logDataToSentry: logDataToSentry,
	});

	Sentry.init({
		environment: config.sentryEnv,
		dsn: config.sentryDsn,
		release: config.sentryRelease || undefined,
		integrations: [
			Sentry.browserProfilingIntegration(),
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect: React.useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
			Sentry.replayIntegration({
				maskAllText: true,
				blockAllMedia: false,
				networkDetailAllowUrls: [window.location.origin, /^https:\/\/api\.getinrev\.com/],
				networkRequestHeaders: ["Content-Type"],
				networkResponseHeaders: ["Content-Type"],
				networkCaptureBodies: false,
			}),
			Sentry.globalHandlersIntegration({ onerror: false, onunhandledrejection: false }),
		],
		profilesSampleRate: 0.2,
		tracesSampleRate: 1.0,
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
	});

	initializeMimeTypes();

	if (config.amplitudeApiKey) {
		initAmplitude(config.amplitudeApiKey);
	}

	ReactDOM.createRoot(document.getElementById("root")!, {
		onUncaughtError: reportReactRootError,
		onCaughtError: reportReactRootError,
	}).render(
		<React.StrictMode>
			<App />
		</React.StrictMode>,
	);
}

loadConfig()
	.then(initializeApp)
	.catch((error) => {
		console.error("Failed to load application configuration:", error);
		document.getElementById("root")!.innerHTML = `
			<div style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: sans-serif;">
				<div style="text-align: center;">
					<h1>Configuration Error</h1>
					<p>Unable to load application configuration. Please try refreshing the page.</p>
				</div>
			</div>
		`;
	});
